import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Pencil, Maximize2, RotateCcw, Camera } from "lucide-react";
import { cn } from "@/lib/utils";

export interface PlacedSticker {
  id: string;
  type: "svg" | "emoji" | "gif" | "upload" | "sticky" | "photo";
  content: string;
  label: string | null;
  /**
   * Free sticker: left edge as % of the layout reference width (1440px on the desktop
   * canvas — a fixed number, so positions never depend on the window size).
   * Anchored sticker: centre as % of the parent card's width (on the card's edge).
   */
  xPct: string;
  /**
   * Free sticker: top edge in px from the canvas origin.
   * Anchored sticker: centre as % of the parent card's height (on the card's edge).
   */
  yPct: string;
  rotation: number;
  scale: string;
  /** Stable id of the journal block this sticker is attached to; null/absent = free sticker. */
  anchorKey?: string | null;
}

/** Only decorative stickers attach to cards; media and sticky notes stay free-standing content. */
export function isAnchorableSticker(type: PlacedSticker["type"]): boolean {
  return type === "svg" || type === "emoji";
}

/**
 * Anchored stickers are centred on their card's border and render at most at 100% scale
 * (≤ 72px), so they reach ≤ ~41px (with rotation) to either side of the edge:
 * - inward: the card pads that side by ANCHOR_EDGE_PAD instead of its usual 16px, so its
 *   own text and header controls sit clear of the sticker (see anchoredEdgeSides);
 * - outward: ~41px minus the 28px gap between cards stays within a neighbour's 16px padding.
 */
export const ANCHOR_EDGE_PAD = 44;
const ANCHOR_MAX_SCALE = 1;
/** Within this distance (px) of a corner, an anchored sticker snaps to the corner. */
const CORNER_SNAP = 28;

/**
 * Which sides of a card need the extra padding for its attached stickers. Corner stickers
 * pad the top/bottom side (enough to clear the corner square for all content).
 */
export function anchoredEdgeSides(stickers: PlacedSticker[]): { top: boolean; right: boolean; bottom: boolean; left: boolean } {
  const sides = { top: false, right: false, bottom: false, left: false };
  for (const st of stickers) {
    const x = parseFloat(st.xPct), y = parseFloat(st.yPct);
    if (y <= 0.5) sides.top = true;
    else if (y >= 99.5) sides.bottom = true;
    else if (x <= 0.5) sides.left = true;
    else if (x >= 99.5) sides.right = true;
  }
  return sides;
}

/**
 * Snap a point (card px) to the card's border, returning it as % of the card size.
 * Anchored stickers live on this border line and are pushed outward when rendered,
 * so they decorate the edge like tape and never sit over the card's text or controls.
 */
export function projectToCardEdge(px: number, py: number, w: number, h: number): { xPct: string; yPct: string } {
  let x = Math.min(Math.max(px, 0), w);
  let y = Math.min(Math.max(py, 0), h);
  const d = { left: x, right: w - x, top: y, bottom: h - y };
  const nearest = (Object.keys(d) as Array<keyof typeof d>).reduce((a, b) => (d[a] <= d[b] ? a : b));
  if (nearest === "left") x = 0;
  else if (nearest === "right") x = w;
  else if (nearest === "top") y = 0;
  else y = h;
  // Close to a corner → take the corner.
  if (nearest === "left" || nearest === "right") {
    if (y < CORNER_SNAP) y = 0; else if (h - y < CORNER_SNAP) y = h;
  } else {
    if (x < CORNER_SNAP) x = 0; else if (w - x < CORNER_SNAP) x = w;
  }
  return { xPct: ((x / Math.max(1, w)) * 100).toFixed(2), yPct: ((y / Math.max(1, h)) * 100).toFixed(2) };
}

/** Where a drag ended, for the parent to decide whether the sticker attaches, detaches or stays. */
export interface StickerDropInfo {
  /** Pointer position in client (screen) px — decides which card (if any) it was dropped on. */
  pointerX: number;
  pointerY: number;
  /** Sticker centre in client (screen) px — where it is placed. */
  centerX: number;
  centerY: number;
  /** Sticker footprint in unscaled px. */
  sizePx: number;
  altKey: boolean;
}

interface StickerOverlayProps {
  stickers: PlacedSticker[];
  /** Attached to the sticker layer itself — the element free stickers are positioned in. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Width that xPct refers to (the layout reference width). */
  referenceWidth: number;
  onMove: (id: string, xPct: string, yPct: string) => void;
  onDelete: (id: string) => void;
  onRotate: (id: string, rotation: number) => void;
  onResize?: (id: string, scale: string) => void;
  onUpdateText?: (id: string, text: string) => void;
  onReplacePhoto?: (id: string, slot: number, file: File) => void;
  /** Called when a drag ends; return true if the parent re-placed the sticker (attach/detach). */
  onDropAt?: (id: string, info: StickerDropInfo) => boolean;
  /** Pointer position while dragging (null when the drag ends), for drop-target hints. */
  onDragMove?: (id: string, point: { x: number; y: number; altKey: boolean } | null) => void;
  readOnly?: boolean;
}

type StickerCallbacks = Omit<StickerOverlayProps, "stickers" | "containerRef" | "referenceWidth" | "readOnly">;

/** Unscaled px per rendered px of `el` — 1 normally, 1/zoom inside the desktop canvas. */
function unscaleFactor(el: HTMLElement): number {
  const w = el.getBoundingClientRect().width;
  return w > 0 && el.offsetWidth > 0 ? el.offsetWidth / w : 1;
}

// ─── Sticky note text colors by SVG background ───────────────────────────────
function getStickyTextColor(content: string): string {
  if (content.includes("#FEF9C3") || content.includes("#D97706")) return "#78350f";
  if (content.includes("#D1FAE5") || content.includes("#059669")) return "#064e3b";
  if (content.includes("#FCE7F3") || content.includes("#DB2777")) return "#831843";
  if (content.includes("#DBEAFE") || content.includes("#2563EB")) return "#1e3a8a";
  if (content.includes("#EDE9FE") || content.includes("#7C3AED")) return "#3b0764";
  return "#374151";
}

function getStickerSize(type: PlacedSticker["type"], sc: number) {
  if (type === "emoji") return Math.round(52 * sc);
  if (type === "gif" || type === "upload") return Math.round(100 * sc);
  if (type === "photo") return Math.round(160 * sc);
  if (type === "sticky") return Math.round(120 * sc);
  return Math.round(72 * sc);
}

function DraggableSticker({
  sticker,
  containerRef,
  onMove,
  onDelete,
  onRotate,
  onResize,
  onUpdateText,
  onReplacePhoto,
  onDropAt,
  onDragMove,
  readOnly,
  mode = "free",
}: StickerCallbacks & {
  sticker: PlacedSticker;
  containerRef: React.RefObject<HTMLDivElement | null>;
  readOnly?: boolean;
  /** "edge": anchored to a card — rendered on the card's border, re-placed by the parent on drop. */
  mode?: "free" | "edge";
}) {
  const [selected, setSelected] = useState(false);
  const [editing, setEditing] = useState(false);
  const [localText, setLocalText] = useState(sticker.label ?? "");
  const [localScale, setLocalScale] = useState(parseFloat(sticker.scale) || 1);
  const [hasDragged, setHasDragged] = useState(false);
  const [photoHovered, setPhotoHovered] = useState(false);
  const dragStart = useRef<{ mouseX: number; mouseY: number; xPct: number; yPct: number } | null>(null);
  const currentDragScaleRef = useRef<number>(localScale);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stickerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // track live rotation during drag without re-renders
  const liveRotationRef = useRef<number>(sticker.rotation);

  // Sync localText when label changes from outside
  useEffect(() => { setLocalText(sticker.label ?? ""); }, [sticker.label]);

  // Sync localScale when scale changes from outside (but not during drag)
  useEffect(() => {
    const incoming = parseFloat(sticker.scale) || 1;
    setLocalScale(incoming);
    currentDragScaleRef.current = incoming;
  }, [sticker.scale]);

  // Sync live rotation ref
  useEffect(() => { liveRotationRef.current = sticker.rotation; }, [sticker.rotation]);

  // Edge-mode drags move the sticker with a temporary `translate`; once the parent has
  // re-placed it (new xPct/yPct), drop the offset in the same frame.
  useLayoutEffect(() => {
    if (stickerRef.current) stickerRef.current.style.translate = "";
  }, [sticker.xPct, sticker.yPct]);
  const clearTranslateSoon = () => {
    setTimeout(() => { if (stickerRef.current) stickerRef.current.style.translate = ""; }, 120);
  };

  const xPct = parseFloat(sticker.xPct);
  const yPct = parseFloat(sticker.yPct);

  // Attached stickers are capped so they always fit their card's padded edge.
  const size = getStickerSize(sticker.type, mode === "edge" ? Math.min(localScale, ANCHOR_MAX_SCALE) : localScale);

  /** Report the drop to the parent; true when it re-placed the sticker (attach/detach). */
  const reportDrop = (altKey: boolean, pointerX: number, pointerY: number): boolean => {
    onDragMove?.(sticker.id, null);
    const el = stickerRef.current;
    if (!el || !onDropAt) return false;
    const r = el.getBoundingClientRect();
    return onDropAt(sticker.id, {
      pointerX,
      pointerY,
      centerX: r.left + r.width / 2,
      centerY: r.top + r.height / 2,
      sizePx: size,
      altKey,
    });
  };

  // ─── Text save ──────────────────────────────────────────────────────────────
  const saveText = useCallback(() => {
    setEditing(false);
    if (onUpdateText) onUpdateText(sticker.id, localText);
  }, [sticker.id, localText, onUpdateText]);

  // ─── Mouse drag (move) ───────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (readOnly || editing) return;
    e.preventDefault();
    e.stopPropagation();
    setHasDragged(false);

    const container = containerRef.current;
    if (!container) return;
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, xPct, yPct };
    // Screen px → container px (the desktop canvas may be zoomed).
    const k = unscaleFactor(container);

    const handleMouseMove = (me: MouseEvent) => {
      if (!dragStart.current || !stickerRef.current || !container) return;
      const dx = me.clientX - dragStart.current.mouseX;
      const dy = me.clientY - dragStart.current.mouseY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) setHasDragged(true);
      onDragMove?.(sticker.id, { x: me.clientX, y: me.clientY, altKey: me.altKey });
      if (mode === "edge") {
        // Follow the pointer visually; the parent decides the final placement on drop.
        stickerRef.current.style.translate = `${dx * k}px ${dy * k}px`;
        return;
      }
      const rect = container.getBoundingClientRect();
      const newX = dragStart.current.xPct + (dx / rect.width) * 100;
      const newY = dragStart.current.yPct + dy * k;
      stickerRef.current.style.left = `${newX}%`;
      stickerRef.current.style.top = `${newY}px`;
    };

    const handleMouseUp = (me: MouseEvent) => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (!dragStart.current || !container) return;
      const dx = me.clientX - dragStart.current.mouseX;
      const dy = me.clientY - dragStart.current.mouseY;
      const moved = Math.abs(dx) > 3 || Math.abs(dy) > 3;
      if (mode === "edge") {
        const handled = moved && reportDrop(me.altKey, me.clientX, me.clientY);
        if (!handled && stickerRef.current) stickerRef.current.style.translate = "";
        else clearTranslateSoon();
        if (!moved) onDragMove?.(sticker.id, null);
      } else if (moved && !reportDrop(me.altKey, me.clientX, me.clientY)) {
        const rect = container.getBoundingClientRect();
        const newX = dragStart.current.xPct + (dx / rect.width) * 100;
        const newY = dragStart.current.yPct + dy * k;
        onMove(sticker.id, newX.toFixed(2), newY.toFixed(0));
      } else if (!moved) {
        onDragMove?.(sticker.id, null);
      }
      dragStart.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [sticker.id, xPct, yPct, containerRef, onMove, readOnly, editing, mode, onDragMove, reportDrop]);

  // ─── Touch support ───────────────────────────────────────────────────────────
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (readOnly || editing) return;
    e.stopPropagation();
    const touch = e.touches[0];
    setHasDragged(false);

    longPressTimer.current = setTimeout(() => { onDelete(sticker.id); }, 600);

    const container = containerRef.current;
    if (!container) return;
    dragStart.current = { mouseX: touch.clientX, mouseY: touch.clientY, xPct, yPct };
    const k = unscaleFactor(container);

    const handleTouchMove = (te: TouchEvent) => {
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
      if (!dragStart.current || !container || !stickerRef.current) return;
      const t = te.touches[0];
      const dx = t.clientX - dragStart.current.mouseX;
      const dy = t.clientY - dragStart.current.mouseY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) setHasDragged(true);
      if (mode === "edge") {
        stickerRef.current.style.translate = `${dx * k}px ${dy * k}px`;
        return;
      }
      const rect = container.getBoundingClientRect();
      const newX = dragStart.current.xPct + (dx / rect.width) * 100;
      const newY = dragStart.current.yPct + dy * k;
      stickerRef.current.style.left = `${newX}%`;
      stickerRef.current.style.top = `${newY}px`;
    };

    const handleTouchEnd = (te: TouchEvent) => {
      if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      if (!dragStart.current || !container) return;
      const t = te.changedTouches[0];
      const dx = t.clientX - dragStart.current.mouseX;
      const dy = t.clientY - dragStart.current.mouseY;
      const moved = Math.abs(dx) > 3 || Math.abs(dy) > 3;
      if (mode === "edge") {
        const handled = moved && reportDrop(false, t.clientX, t.clientY);
        if (!handled && stickerRef.current) stickerRef.current.style.translate = "";
        else clearTranslateSoon();
      } else if (moved && !reportDrop(false, t.clientX, t.clientY)) {
        const rect = container.getBoundingClientRect();
        const newX = dragStart.current.xPct + (dx / rect.width) * 100;
        const newY = dragStart.current.yPct + dy * k;
        onMove(sticker.id, newX.toFixed(2), newY.toFixed(0));
      }
      dragStart.current = null;
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
  }, [sticker.id, xPct, yPct, containerRef, onMove, onDelete, readOnly, editing, mode, reportDrop]);

  // ─── Resize handle drag ───────────────────────────────────────────────────────
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startScale = currentDragScaleRef.current;
    const k = containerRef.current ? unscaleFactor(containerRef.current) : 1;

    const handleMouseMove = (me: MouseEvent) => {
      const dy = (me.clientY - startY) * k;
      const newScale = Math.max(0.3, Math.min(5.0, startScale - dy / 80));
      currentDragScaleRef.current = newScale;
      setLocalScale(newScale);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (onResize) onResize(sticker.id, currentDragScaleRef.current.toFixed(2));
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [sticker.id, onResize, containerRef]);

  // ─── Smooth drag rotation ────────────────────────────────────────────────────
  const handleRotateMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const el = stickerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    const startRotation = liveRotationRef.current;

    const handleMouseMove = (me: MouseEvent) => {
      const currentAngle = Math.atan2(me.clientY - centerY, me.clientX - centerX) * (180 / Math.PI);
      const delta = currentAngle - startAngle;
      const newRotation = ((startRotation + delta) % 360 + 360) % 360;
      liveRotationRef.current = newRotation;
      if (el) el.style.transform = `rotate(${newRotation}deg)`;
    };

    const handleMouseUp = (me: MouseEvent) => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      const currentAngle = Math.atan2(me.clientY - centerY, me.clientX - centerX) * (180 / Math.PI);
      const delta = currentAngle - startAngle;
      const newRotation = Math.round(((startRotation + delta) % 360 + 360) % 360);
      liveRotationRef.current = newRotation;
      onRotate(sticker.id, newRotation);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [sticker.id, onRotate]);

  // ─── Click outside to deselect ───────────────────────────────────────────────
  useEffect(() => {
    if (!selected && !editing) return;
    const handler = (e: MouseEvent) => {
      if (stickerRef.current && !stickerRef.current.contains(e.target as Node)) {
        if (editing) saveText();
        setSelected(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [selected, editing, saveText]);

  // ─── Click handler ────────────────────────────────────────────────────────────
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly || hasDragged) return;
    if (sticker.type === "sticky") {
      if (selected && !editing) {
        setEditing(true);
        setTimeout(() => textareaRef.current?.focus(), 20);
      } else {
        setSelected(true);
      }
    } else {
      setSelected(true);
    }
  }, [readOnly, hasDragged, sticker.type, selected, editing]);

  // ─── Content rendering ────────────────────────────────────────────────────────
  const renderContent = () => {
    if (sticker.type === "emoji") {
      return (
        <span style={{ fontSize: `${Math.round(36 * (mode === "edge" ? Math.min(localScale, ANCHOR_MAX_SCALE) : localScale))}px`, lineHeight: 1, userSelect: "none", display: "block" }}>
          {sticker.content}
        </span>
      );
    }

    if (sticker.type === "gif" || sticker.type === "upload") {
      return (
        <img
          src={sticker.content}
          alt={sticker.label ?? "sticker"}
          style={{ width: size, height: size, objectFit: "contain", display: "block", pointerEvents: "none" }}
          draggable={false}
        />
      );
    }

    if (sticker.type === "photo") {
      const slot = parseInt(sticker.label ?? "0", 10);
      return (
        <div
          className="relative"
          style={{ width: size, height: size }}
          onMouseEnter={() => setPhotoHovered(true)}
          onMouseLeave={() => setPhotoHovered(false)}
        >
          <img
            src={sticker.content}
            alt="Journal photo"
            style={{
              width: size,
              height: size,
              objectFit: "cover",
              borderRadius: 10,
              boxShadow: "2px 4px 16px rgba(0,0,0,0.22)",
              display: "block",
              pointerEvents: "none",
              filter: "saturate(0.88) contrast(1.04)",
            }}
            draggable={false}
          />
          {photoHovered && onReplacePhoto && !readOnly && (
            <button
              className="absolute inset-0 flex flex-col items-center justify-center gap-1 transition-all"
              style={{ background: "rgba(0,0,0,0.42)", borderRadius: 10 }}
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              onMouseDown={(e) => e.stopPropagation()}
              title="Replace photo"
            >
              <Camera className="w-5 h-5 text-white drop-shadow" />
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "white", fontWeight: 600 }}>Replace</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f && onReplacePhoto) onReplacePhoto(sticker.id, slot, f);
              e.target.value = "";
            }}
          />
        </div>
      );
    }

    if (sticker.type === "sticky") {
      const textColor = getStickyTextColor(sticker.content);
      const padding = Math.round(size * 0.14);
      const topOffset = Math.round(size * 0.20);
      const textAreaW = Math.round(size * 0.76);
      const textAreaH = Math.round(size * 0.70);
      const fontSize = Math.max(11, Math.round(size * 0.115));

      return (
        <div className="relative" style={{ width: size, height: size }}>
          <div style={{ width: size, height: size, pointerEvents: "none" }}
            dangerouslySetInnerHTML={{ __html: sticker.content }} />
          <div className="absolute" style={{ top: topOffset, left: padding, width: textAreaW, height: textAreaH }}>
            {editing && !readOnly ? (
              <textarea
                ref={textareaRef}
                value={localText}
                onChange={(e) => setLocalText(e.target.value)}
                onBlur={saveText}
                onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); saveText(); } e.stopPropagation(); }}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder="Write something…"
                className="w-full h-full bg-transparent border-none outline-none resize-none placeholder:opacity-40"
                style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize, color: textColor, lineHeight: 1.55, padding: 0 }}
              />
            ) : (
              <div
                className="w-full h-full overflow-hidden cursor-text select-none"
                style={{ fontFamily: "'Kalam', cursive", fontWeight: 400, fontSize, color: localText ? textColor : `${textColor}55`, lineHeight: 1.55, wordBreak: "break-word", whiteSpace: "pre-wrap" }}
              >
                {localText || (selected ? "tap again to write…" : "tap to select")}
              </div>
            )}
          </div>
          {selected && !editing && !readOnly && (
            <div className="absolute flex items-center gap-1 bg-white/90 rounded-full px-2 py-0.5 shadow-sm border border-stone-200"
              style={{ bottom: -22, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>
              <Pencil className="w-2.5 h-2.5 text-stone-500" />
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 9, color: "#6b7280" }}>tap again to write</span>
            </div>
          )}
        </div>
      );
    }

    // Default SVG
    return (
      <div style={{ width: size, height: size, pointerEvents: "none" }}
        dangerouslySetInnerHTML={{ __html: sticker.content }} />
    );
  };

  // Edge mode: (xPct, yPct) is a point on the card border; the sticker is centred on it
  // (the card pads that side — see ANCHOR_EDGE_PAD).
  let position: React.CSSProperties;
  if (mode === "edge") {
    position = {
      left: `calc(${xPct}% - ${size / 2}px)`,
      top: `calc(${yPct}% - ${size / 2}px)`,
      width: size,
      height: size,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };
  } else {
    position = { left: `${xPct}%`, top: `${yPct}px` };
  }

  return (
    <div
      ref={stickerRef}
      data-canvas-item=""
      data-canvas-key={`sticker:${sticker.id}`}
      className={cn("absolute z-20", editing ? "cursor-text" : "cursor-grab")}
      style={{
        ...position,
        transform: `rotate(${sticker.rotation}deg)`,
        transformOrigin: "center center",
        touchAction: "none",
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
    >
      {renderContent()}

      {/* Selection controls */}
      <AnimatePresence>
        {selected && !editing && !readOnly && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute -inset-2 rounded-xl border-2 border-violet-400 border-dashed pointer-events-none"
          >
            {/* Delete — top right */}
            <button
              className="absolute -top-3.5 -right-3.5 w-7 h-7 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-md pointer-events-auto transition-colors"
              onClick={(e) => { e.stopPropagation(); onDelete(sticker.id); }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Trash2 className="w-3.5 h-3.5 text-white" />
            </button>

            {/* Rotate — bottom right (drag to rotate freely) */}
            <button
              className="absolute -bottom-3.5 -right-3.5 w-7 h-7 bg-violet-500 hover:bg-violet-600 rounded-full flex items-center justify-center shadow-md pointer-events-auto transition-colors cursor-grab active:cursor-grabbing"
              title="Drag to rotate freely"
              onMouseDown={(e) => { e.stopPropagation(); handleRotateMouseDown(e); }}
              onClick={(e) => e.stopPropagation()}
            >
              <RotateCcw className="w-3.5 h-3.5 text-white" />
            </button>

            {/* Resize — bottom left (drag up/down) */}
            {onResize && (
              <button
                className="absolute -bottom-3.5 -left-3.5 w-7 h-7 bg-teal-500 hover:bg-teal-600 rounded-full flex items-center justify-center shadow-md pointer-events-auto transition-colors cursor-ns-resize"
                title="Drag up to grow, drag down to shrink"
                onMouseDown={(e) => { e.stopPropagation(); handleResizeMouseDown(e); }}
                onClick={(e) => e.stopPropagation()}
              >
                <Maximize2 className="w-3.5 h-3.5 text-white" />
              </button>
            )}
          </motion.div>
        )}
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute -inset-2 rounded-xl border-2 border-amber-400 border-dashed pointer-events-none"
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Free stickers on the desktop canvas. The layer is a zero-height strip starting at the
 * canvas origin and `referenceWidth` wide: it only gives xPct a fixed meaning (and lets
 * drags read the zoom) — stickers overflow it freely in every direction.
 */
export function StickerOverlay({
  stickers,
  containerRef,
  referenceWidth,
  readOnly,
  ...callbacks
}: StickerOverlayProps) {
  return (
    <div
      ref={containerRef}
      data-sticker-layer
      className="absolute left-0 top-0 h-0 pointer-events-none z-20"
      style={{ width: referenceWidth, overflow: "visible" }}
    >
      {stickers.map((sticker) => (
        <div key={sticker.id} className="pointer-events-auto">
          <DraggableSticker sticker={sticker} containerRef={containerRef} readOnly={readOnly} {...callbacks} />
        </div>
      ))}
    </div>
  );
}

/**
 * Stickers attached to one journal card (desktop). Rendered inside the card so they move
 * with it; each sits on the card's border (see projectToCardEdge) rather than over its text.
 */
export function AnchoredStickers({
  stickers,
  readOnly,
  ...callbacks
}: Omit<StickerOverlayProps, "containerRef" | "referenceWidth">) {
  const layerRef = useRef<HTMLDivElement>(null);
  if (stickers.length === 0) return null;
  return (
    <div ref={layerRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 5 }}>
      {stickers.map((sticker) => (
        <div key={sticker.id} className="pointer-events-auto">
          <DraggableSticker sticker={sticker} containerRef={layerRef} readOnly={readOnly} mode="edge" {...callbacks} />
        </div>
      ))}
    </div>
  );
}

/**
 * Non-interactive sticker, laid out in normal flow at roughly `size` px — used by the
 * mobile card stack, where stickers are contained content rather than floating objects.
 */
export function StaticSticker({ sticker, size }: { sticker: PlacedSticker; size: number }) {
  const box: React.CSSProperties = { width: size, height: size, flexShrink: 0, transform: `rotate(${sticker.rotation}deg)` };
  if (sticker.type === "emoji") {
    return (
      <span style={{ ...box, fontSize: Math.round(size * 0.72), lineHeight: `${size}px`, textAlign: "center", display: "inline-block" }}>
        {sticker.content}
      </span>
    );
  }
  if (sticker.type === "gif" || sticker.type === "upload" || sticker.type === "photo") {
    return <img src={sticker.content} alt={sticker.label ?? "sticker"} draggable={false} style={{ ...box, objectFit: "contain" }} />;
  }
  if (sticker.type === "sticky") {
    const textColor = getStickyTextColor(sticker.content);
    return (
      <div className="relative" style={box}>
        <div className="absolute inset-0 [&>svg]:w-full [&>svg]:h-full" dangerouslySetInnerHTML={{ __html: sticker.content }} />
        <div
          className="absolute overflow-hidden"
          style={{
            top: size * 0.2, left: size * 0.14, width: size * 0.76, height: size * 0.7,
            fontFamily: "'Kalam', cursive", fontSize: Math.max(12, Math.round(size * 0.1)), lineHeight: 1.55,
            color: textColor, whiteSpace: "pre-wrap", wordBreak: "break-word",
          }}
        >
          {sticker.label}
        </div>
      </div>
    );
  }
  return <div className="[&>svg]:w-full [&>svg]:h-full" style={box} dangerouslySetInnerHTML={{ __html: sticker.content }} />;
}

export { getStickyTextColor };
